/**
 * Force Update Manager
 *
 * Handles force update and service maintenance checks.
 * This manager provides:
 * - Force update status checks from Statsig
 * - Service maintenance detection
 * - App version requirement validation
 * - Banner and dialog display logic
 *
 * Called during app initialization to determine if the app should be blocked
 * due to required updates or service maintenance.
 */

import { setDynamicData } from 'services/forceUpdate/helpers';
import { AppUpdates } from 'services/forceUpdate/types';
import { getDynamicConfiguration } from 'services/statsig/statsigManager';
import {
  isAndroid as defaultIsAndroid,
  isWeb as defaultIsWeb,
} from 'shared/constants/platform';
import { DynamicConfigurationKey } from 'shared/providers/statsig/StatsigProvider.d';
import { defaultAppVersion } from 'shared/utils/appVersionUtils';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

export interface ForceUpdateResult {
  shouldBlock: boolean;
  isServiceDown: boolean;
  isUpdateRequired: boolean;
  isUpdateNeeded: boolean;
  dueToMinSupportedVersion: boolean;
  dueToTargetSupportedVersion: boolean;
  rawConfig: AppUpdates;
}

export interface ForceUpdatePlatform {
  isAndroid: boolean;
  isWeb: boolean;
}

/**
 * Check force update status from Statsig
 * Called during initialization, before showing any UI
 *
 * @returns Force update result containing blocking status and UI display flags
 */
/**
 * Check if app version is below the required version
 */
function isVersionBelowRequired(
  appVersion: string,
  requiredVersion: string,
): boolean {
  const [appMajor, appMinor, appPatch] = appVersion.split('.').map(Number);
  const [reqMajor, reqMinor, reqPatch] = requiredVersion.split('.').map(Number);

  if (appMajor !== reqMajor) {
    return appMajor < reqMajor;
  }
  if (appMinor !== reqMinor) {
    return appMinor < reqMinor;
  }
  return appPatch < reqPatch;
}

export async function checkForceUpdate(
  platform: ForceUpdatePlatform = {
    isAndroid: defaultIsAndroid,
    isWeb: defaultIsWeb,
  },
): Promise<ForceUpdateResult> {
  try {
    const appVersion =
      getEnvironmentVar('EXPO_APP_VERSION') ?? defaultAppVersion;

    // Get dynamic config from Statsig (raw, untranslated)
    const dynamicConfig = getDynamicConfiguration(
      DynamicConfigurationKey.SERVER_STATUS,
    );
    const rawConfig = JSON.parse(JSON.stringify(dynamicConfig)) as AppUpdates;

    if (Object.keys(rawConfig).length === 0) {
      logBreadcrumb({
        level: 'warning',
        message: 'Force update config unavailable - using defaults',
        data: { configKey: DynamicConfigurationKey.SERVER_STATUS },
      });
    }

    // Populate module-level state so helper functions can access the config
    setDynamicData(rawConfig);

    // Check service maintenance directly from raw config
    const isServiceDown =
      rawConfig.serviceMaintenance?.showMessageInApp ?? false;

    // Check version requirements from raw config (platform-specific)
    const getPlatformVersions = () => {
      if (platform.isWeb) {
        return null;
      }
      return platform.isAndroid
        ? rawConfig.supportedVersions?.android
        : rawConfig.supportedVersions?.ios;
    };
    const platformVersions = getPlatformVersions();

    const dueToMinSupportedVersion = platformVersions
      ? isVersionBelowRequired(appVersion, platformVersions.minSupportedVersion)
      : false;
    const dueToTargetSupportedVersion = platformVersions
      ? isVersionBelowRequired(appVersion, platformVersions.targetVersion)
      : false;
    const isUpdateNeeded =
      dueToMinSupportedVersion || dueToTargetSupportedVersion;

    const result: ForceUpdateResult = {
      // Service maintenance blocks all platforms
      // App updates only block native (web can't be force updated)
      shouldBlock: isServiceDown || (!platform.isWeb && isUpdateNeeded),
      isServiceDown,
      isUpdateRequired: false, // Legacy field, kept for compatibility
      isUpdateNeeded,
      dueToMinSupportedVersion,
      dueToTargetSupportedVersion,
      rawConfig,
    };

    logBreadcrumb({
      level: 'info',
      message: 'Force update check completed',
      data: result,
    });

    return result;
  } catch (error) {
    logBreadcrumb({
      level: 'error',
      message: 'Force update check failed',
      data: { error },
    });

    // Safe fallback - don't block the app
    return {
      shouldBlock: false,
      isServiceDown: false,
      isUpdateRequired: false,
      isUpdateNeeded: false,
      dueToMinSupportedVersion: false,
      dueToTargetSupportedVersion: false,
      rawConfig: {} as AppUpdates,
    };
  }
}
